import * as React from "react";
import { cn } from "~/lib/utils";
import { NavigationUserEvents } from "~/modules/analytics/events/navigation";
import NextLink from "next/link";
import { track } from "~/modules/analytics/track";

// // // //

export type LinkVariant = "default" | "unstyled";

const variantClasses: Record<LinkVariant, string> = {
    default: "text-link hover:text-link-hover transition-colors",
    unstyled: "",
};

// // // //

interface AnalyticsProps {
    analyticsLabel: string | null;
    analyticsEventProperties?: Record<string, any>;
    onClick?: never;
}
interface OnClickProps {
    analyticsLabel?: never;
    analyticsEventProperties?: never;
    onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
}
type ClickableProps = AnalyticsProps | OnClickProps;

interface Props {
    href: string;
    ariaLabel?: string;
    openInNewTab?: boolean;
    draggable?: boolean;
    variant?: LinkVariant;
    className?: string;
    stopPropagation?: boolean;
    prefetch?: boolean;
    children: React.ReactNode;
    setTriggerRef?: React.Dispatch<React.SetStateAction<HTMLElement | null>>; // NOTE - used when triggering a Tooltip from this component
}

/**
 * Link
 * Implements next/link to navigate between pages internal to the app
 */
export function Link(props: Props & ClickableProps) {
    const {
        href,
        ariaLabel,
        analyticsLabel = null,
        analyticsEventProperties = {},
        openInNewTab = false,
        draggable,
        variant = "unstyled",
        stopPropagation = false,
        className,
        children,
        onClick,
        setTriggerRef,
        prefetch = false,
    } = props;

    const resolvedClassName = cn(variantClasses[variant], className);

    return (
        <NextLink
            href={href}
            className={resolvedClassName}
            aria-label={ariaLabel}
            draggable={draggable}
            prefetch={prefetch}
            ref={setTriggerRef}
            target={openInNewTab ? "_blank" : undefined}
            rel={openInNewTab ? "noopener" : undefined}
            onClick={(e) => {
                // Handle props.stopPropagation
                if (stopPropagation) {
                    e.stopPropagation();
                }

                // Default analytics will be skipped in props.onClick is passed in
                if (onClick) {
                    onClick(e);
                    return;
                }

                // Track the internalLinkClck event + props
                if (analyticsLabel !== null) {
                    track(NavigationUserEvents.internalLinkClick, {
                        href,
                        label: analyticsLabel,
                        ...analyticsEventProperties,
                    });
                }
            }}
        >
            {children}
        </NextLink>
    );
}
